import type { SymmetryAxis } from "@pdfking/filling"
import type { Distance } from "@pdfking/math"
import { Max, Min } from "@pdfking/math"
import { ZERO_DISTANCE } from "@pdfking/math/constants"
import { MM } from "@pdfking/quantity"

import type { BoxPointList, BoxPointLists, GrowPlotter, PlotContribute } from "#internal"
import { CUT_LAYER_ID, FOLD_LAYER_ID } from "#internal"

import { FLAP_FOR_CLOSE_SIZE, FLAP_FOR_GLUE_SIZE, FLAP_SIZE } from "./BoxConstants"
import { ExtractVariables } from "./ExtractVariables"
import { PlotPointsList } from "./PlotPointsList"
import { PointAccumulate } from "./PointAccumulate"

const min_coord = (points_list: BoxPointLists, axis: SymmetryAxis): Distance =>
  Min(...points_list.flatMap(points => points.map(point => point[axis]))) as Distance

export const PlotBoxSimple: GrowPlotter = size => {
  const { w: h, h: w, d: bd, s: sd } = ExtractVariables(size)
  const l = Max(MM(2), sd) as Distance
  const ll = Min(0.5 * w - 2 * sd - MM(2), FLAP_FOR_CLOSE_SIZE, bd * 0.8) as Distance

  const cut_layer = PointAccumulate(
    [
      { x: 0, y: 0 },
      { x: -sd, y: -2 * sd },
      { x: 0, y: 2 * sd - bd },
      { x: 2 * sd + MM(3), y: 0 },
    ] as BoxPointList,
    [
      { x: sd, y: -bd },
      { x: MM(3), y: -FLAP_SIZE },
      { x: w - 2 * sd - MM(6), y: 0 },
      { x: MM(3), y: FLAP_SIZE },
    ] as BoxPointList,
    [
      { x: w - sd - MM(3), y: -bd },
      { x: 2 * sd + MM(3), y: 0 },
      { x: 0, y: bd - 2 * sd },
      { x: -sd, y: 2 * sd },
    ] as BoxPointList,
    [
      { x: w + sd, y: -ll },
      { x: 0.9 * bd - sd, y: 0 },
      { x: 0.1 * bd, y: ll - 0.1 * bd },
      { x: 0, y: 0.1 * bd },
      { x: 0, y: h + 0.1 * bd },
      { x: -0.1 * bd, y: ll - 0.1 * bd },
      { x: sd - 0.9 * bd, y: 0 },
    ] as BoxPointList,
    [
      { x: w, y: h },
      { x: sd, y: 2 * sd },
      { x: 0, y: bd - 2 * sd },
      { x: -2 * sd - MM(3), y: 0 },
    ] as BoxPointList,
    [
      { x: w - sd, y: h + bd },
      { x: -MM(3), y: FLAP_SIZE },
      { x: 2 * sd - w + MM(6), y: 0 },
      { x: -MM(3), y: -FLAP_SIZE },
    ] as BoxPointList,
    [
      { x: sd + MM(3), y: h + bd },
      { x: -2 * sd - MM(3), y: 0 },
      { x: 0, y: 2 * sd - bd },
      { x: sd, y: -2 * sd },
    ] as BoxPointList,
    [
      { x: -sd, y: h + ll },
      { x: sd - 0.9 * bd + l, y: 0 }, // x: -bd + sd + l + 0.1 * bd
      { x: -0.1 * bd, y: bd / 10 - ll },
      { x: 0, y: -0.1 * bd },
      { x: -l, y: 0 },
    ] as BoxPointList,
    [
      { x: -bd, y: h },
      { x: -2 * sd, y: 2 * sd },
      { x: 4 * sd - w, y: 0 },
      { x: -2 * sd, y: -2 * sd },
      { x: -FLAP_FOR_GLUE_SIZE, y: -MM(3) },
      { x: 0, y: MM(6) - h },
      { x: FLAP_FOR_GLUE_SIZE, y: -MM(3) },
      { x: 2 * sd, y: -2 * sd },
      { x: w - 4 * sd, y: 0 },
      { x: 2 * sd, y: 2 * sd },
      { x: l, y: 0 },
      { x: 0, y: -0.1 * bd },
      { x: 0.1 * bd, y: bd / 10 - ll },
      { x: 0.9 * bd - sd - l, y: 0 }, // x: bd - sd - l - 0.1 * bd
    ] as BoxPointList
  )

  const offset_x = min_coord(cut_layer, "x")
  const offset_y = min_coord(cut_layer, "y")

  const fold_layer = PointAccumulate(
    [
      { x: l - bd, y: 0 },
      { x: bd - l, y: 0 },
      { x: 0, y: h },
      { x: l - bd, y: 0 },
    ] as BoxPointList,
    [
      { x: -sd, y: h + 2 * sd },
      { x: w + 2 * sd, y: 0 },
    ] as BoxPointList,
    [
      { x: -bd, y: h },
      { x: 0, y: -h },
    ] as BoxPointList,
    [
      { x: sd + MM(3), y: h + bd },
      { x: w - 2 * sd - MM(6), y: 0 },
    ] as BoxPointList,
    [
      { x: -bd - w, y: 0 },
      { x: 0, y: h },
    ] as BoxPointList,
    [
      { x: w + bd, y: 0 },
      { x: -bd, y: 0 },
      { x: 0, y: h },
    ] as BoxPointList,
    [
      { x: w + bd, y: h },
      { x: -bd, y: 0 },
    ] as BoxPointList,
    [
      { x: -sd, y: -2 * sd },
      { x: w + 2 * sd, y: 0 },
    ] as BoxPointList,
    [
      { x: sd + MM(3), y: -bd },
      { x: w - 2 * sd - MM(6), y: 0 },
    ] as BoxPointList
  )

  return {
    [CUT_LAYER_ID]: [...PlotPointsList(cut_layer, "cut", { offset_x, offset_y })],
    [FOLD_LAYER_ID]: [...PlotPointsList(fold_layer, "fold", { offset_x, offset_y })],
  } as PlotContribute
}

export const BoxSimpleOpeningGrow = (
  _width: Distance,
  height: Distance,
  depth: Distance = ZERO_DISTANCE
): Distance => (FLAP_FOR_GLUE_SIZE + 2 * (height + depth)) as Distance

export const BoxSimpleReverseGrow = (
  _height: Distance,
  width: Distance,
  depth: Distance = ZERO_DISTANCE
): Distance => ((FLAP_SIZE + depth) * 2 + width) as Distance
